<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import MessengerHistorySyncAPI from './api';

const props = defineProps({
  inboxId: { type: [String, Number], default: null },
  variant: { type: String, default: 'settings' },
});

const { t } = useI18n();

const status = ref('idle');
const processed = ref(0);
const total = ref(0);
const finishedAt = ref(null);
const pollTimer = ref(null);
const coolingTick = ref(0);

const running = computed(
  () => status.value === 'queued' || status.value === 'running'
);

const cooling = computed(() => {
  coolingTick.value;
  if (!finishedAt.value || running.value) return false;
  const elapsed = Date.now() - new Date(finishedAt.value).getTime();
  return Number.isFinite(elapsed) && elapsed < 60_000;
});

const busy = computed(() => running.value || cooling.value);

const tooltip = computed(() => {
  if (running.value) {
    const current = processed.value;
    const max = total.value;
    if (max > 0) {
      return t('CUSTOM.MESSENGER_HISTORY_SYNC.TOOLTIP_RUNNING_PROGRESS', {
        current,
        total: max,
      });
    }
    return t('CUSTOM.MESSENGER_HISTORY_SYNC.TOOLTIP_RUNNING');
  }
  return t('CUSTOM.MESSENGER_HISTORY_SYNC.TOOLTIP_IDLE');
});

const label = computed(() =>
  running.value
    ? t('CUSTOM.MESSENGER_HISTORY_SYNC.RUNNING')
    : t('CUSTOM.MESSENGER_HISTORY_SYNC.BUTTON')
);

const applyStatus = data => {
  if (!data) return;
  status.value = data.status || 'idle';
  processed.value = Number(data.processed || 0);
  total.value = Number(data.total || 0);
  finishedAt.value = data.finished_at || null;
};

const stopPoll = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

const loadStatus = async () => {
  if (!props.inboxId) return null;
  const { data } = await MessengerHistorySyncAPI.getStatus(props.inboxId);
  applyStatus(data);
  return data;
};

const startPoll = () => {
  if (pollTimer.value) return;
  pollTimer.value = setInterval(async () => {
    try {
      const data = await loadStatus();
      if (!data) return;
      if (data.status === 'finished') {
        stopPoll();
        useAlert(
          t('CUSTOM.MESSENGER_HISTORY_SYNC.SUCCESS', {
            contacts: data.contacts_imported || 0,
            messages: data.messages_imported || 0,
          })
        );
      } else if (data.status === 'error') {
        stopPoll();
        useAlert(t('CUSTOM.MESSENGER_HISTORY_SYNC.FAILED'));
      } else if (data.status !== 'queued' && data.status !== 'running') {
        stopPoll();
      }
    } catch {
      // Keep polling while the job is still running server-side.
    }
  }, 2000);
};

const startSync = async () => {
  if (!props.inboxId || busy.value) return;
  try {
    const { data } = await MessengerHistorySyncAPI.start(props.inboxId);
    applyStatus(data);
    startPoll();
  } catch (error) {
    const code = error?.response?.status;
    const data = error?.response?.data;
    applyStatus(data);
    if (code === 409) {
      useAlert(t('CUSTOM.MESSENGER_HISTORY_SYNC.ALREADY_RUNNING'));
      startPoll();
      return;
    }
    if (code === 429) {
      useAlert(t('CUSTOM.MESSENGER_HISTORY_SYNC.COOLDOWN'));
      return;
    }
    useAlert(t('CUSTOM.MESSENGER_HISTORY_SYNC.FAILED'));
  }
};

watch(
  () => props.inboxId,
  async () => {
    stopPoll();
    status.value = 'idle';
    if (!props.inboxId) return;
    try {
      const data = await loadStatus();
      if (data && (data.status === 'queued' || data.status === 'running')) {
        startPoll();
      }
    } catch {
      status.value = 'idle';
    }
  },
  { immediate: true }
);

onMounted(() => {
  const id = setInterval(() => {
    coolingTick.value += 1;
  }, 5000);
  onBeforeUnmount(() => clearInterval(id));
});

onBeforeUnmount(stopPoll);
</script>

<template>
  <span v-tooltip.top="tooltip" class="inline-flex">
    <Button
      v-if="variant === 'list'"
      icon="i-lucide-refresh-cw"
      slate
      sm
      :disabled="busy"
      :is-loading="running"
      @click="startSync"
    />
    <Button
      v-else
      faded
      slate
      sm
      :label="label"
      :disabled="busy"
      :is-loading="running"
      @click="startSync"
    />
  </span>
</template>
